import React, { Fragment, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import CustomizerContext from "../../_helper/Customizer";
import PrivilegeContext from "../../_helper/PrivilegeContext";
import { useLab } from "../../_helper/PharmacyProvider";
import { MENUITEMS } from "./Menu";
import { useLicense } from "../../_helper/LicenseContext";
import { Tooltip } from 'react-tooltip';


const FEATURE_MENU_MAP = {
  custom_reports: [
    "header-setting",
    "footer-setting",
  ],
};

const SidebarMenuItems = ({ sidebartoogle }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { layout } = useContext(CustomizerContext);
  const { privileges } = useContext(PrivilegeContext);
  const { currentLab } = useLab();
  const { decodeTokenOnly } = useLicense();

  const [filteredMenu, setFilteredMenu] = useState([]);
  const [openSubmenuKey, setOpenSubmenuKey] = useState(null);
  const [licensedFeatures, setLicensedFeatures] = useState({});

  const CurrentPath = location.pathname;
  const layout1 = localStorage.getItem("sidebar_layout") || layout;

  /* ---------------- LICENSE DECODE ---------------- */
  useEffect(() => {
    decodeTokenOnly()
      .then((decoded) => {
        setLicensedFeatures(decoded?.features || {});
      })
      .catch((err) => {
        console.error("License decode failed:", err);
        setLicensedFeatures({});
      });
  }, [decodeTokenOnly]);

  /* ---------------- LICENSE CHECK ---------------- */
  const isMenuAllowedByLicense = (menuKey) => {
    if (!licensedFeatures || Object.keys(licensedFeatures).length === 0) {
      return true;
    }

    const mappedFeature = Object.entries(FEATURE_MENU_MAP).find(([_, keys]) =>
      keys.includes(menuKey)
    );

    if (!mappedFeature) return true;

    const [featureKey] = mappedFeature;
    return licensedFeatures[featureKey] === true;
  };

  /* ---------------- PRIVILEGE FILTER ---------------- */
  const filterMenuItems = (menuItems, permissions) => {
    return menuItems
      .flatMap((menuGroup) => {
        if (!menuGroup.Items) return [];

        return menuGroup.Items.filter((menuItem) => {
          const privilege = permissions.find((p) => p.menu_key === menuItem.key);
          if (!privilege) return false;

          if (privilege.full_access) return true;

          if (menuItem.children?.length > 0) {
            const filteredChildren = menuItem.children.filter((child) => {
              const childPrivilege = privilege.sub_permissions?.find(
                (sp) => sp.key === child.key
              );
              return (
                childPrivilege &&
                (childPrivilege.full_access ||
                  childPrivilege.actions?.some((a) => a.allowed))
              );
            });

            if (filteredChildren.length > 0) {
              menuItem = { ...menuItem, children: filteredChildren };
              return true;
            }
            return false;
          }

          return privilege.actions?.some((a) => a.allowed);
        });
      })
      .map((item) => ({ ...item }));
  };

  /* ---------------- APPLY ALL FILTERS ---------------- */
  useEffect(() => {
    if (!currentLab) {
      setFilteredMenu(MENUITEMS);
      return;
    }

    let menuCopy = JSON.parse(JSON.stringify(MENUITEMS));

    if (privileges?.permissions) {
      menuCopy = menuCopy.map((group) => ({
        ...group,
        Items: filterMenuItems([group], privileges.permissions),
      }));
    }

    const isNonAdmin = currentLab?.isAdmin === false;

    const hiddenForNonAdmin = [
      "lab-user",
      "pay-in-out",
      "collection",
      "transaction",
    ];

    menuCopy = menuCopy.map((group) => ({
      ...group,
      Items: group.Items
        .filter((item) => {
          if (hiddenForNonAdmin.includes(item.key)) {
            return !isNonAdmin;
          }
          return true;
        })
        .map((item) => {
          if (item.children && item.children.length > 0) {
            const filteredChildren = item.children.filter((child) => {
              if (hiddenForNonAdmin.includes(child.key)) {
                return currentLab?.isAdmin === true;
              }
              return isMenuAllowedByLicense(child.key);
            });

            if (filteredChildren.length === 0) {
              return null;
            }

            return { ...item, children: filteredChildren };
          }
          return item;
        })
        .filter(Boolean)
        .filter((item) => {
          return item.type !== "sub" || (item.children && item.children.length > 0);
        }),
    }));

    menuCopy = menuCopy.filter((group) => group.Items.length > 0);

    setFilteredMenu(menuCopy);
  }, [currentLab, privileges?.permissions, licensedFeatures]);

  /* ---------------- ACCORDION LOGIC ---------------- */

  const toggleSubmenu = (key) => {
    setOpenSubmenuKey((prevKey) => (prevKey === key ? null : key));
  };

  const handleNavClick = () => {
    if (window.innerWidth <= 991 && sidebartoogle) {
      sidebartoogle(false);
      document.querySelector(".page-header")?.classList.add("close_icon");
      document.querySelector(".sidebar-wrapper")?.classList.add("close_icon");
    }
  };

  const isPathActive = (path) => CurrentPath.startsWith(path);

  /* ---------------- RENDER MENU ---------------- */
  return (
    <>
      {filteredMenu.map((ItemGroup, i) => (
        <Fragment key={i}>
          {ItemGroup.Items.map((menuItem, j) => {
            const isSubmenuOpen = openSubmenuKey === menuItem.key;
            const hasActiveChild = menuItem.children?.some((child) =>
              isPathActive(child.path)
            );
            const isActive = isPathActive(menuItem.path) || hasActiveChild;

            return (
              <li className={`sidebar-list ${isActive ? "active" : ""}`} key={j}>
                {/* Submenu Parent */}
                {menuItem.type === "sub" && (
                  <a
                    href="#javascript"
                    className={`sidebar-link sidebar-title ${isActive || isSubmenuOpen ? "active" : ""
                      }`}
                    onClick={(e) => {
                      e.preventDefault();
                      toggleSubmenu(menuItem.key);
                    }}
                  >
                    <i className={`${menuItem.icon} text-white me-3`} />
                    <span>{t(menuItem.title)}</span>
                    <div className="according-menu">
                      <i
                        className={`fa ${isSubmenuOpen ? "fa-angle-down" : "fa-angle-right"
                          }`}
                      />
                    </div>
                  </a>
                )}

                {/* Direct Link */}
                {menuItem.type === "link" && (
                  <Link
                    to={menuItem.path}
                    className={`sidebar-link sidebar-title link-nav ${isPathActive(menuItem.path) ? "active" : ""
                      }`}
                    onClick={handleNavClick}
                  >
                    <i className={`${menuItem.icon} text-white me-3`} />
                    <span>{t(menuItem.title)}</span>
                  </Link>
                )}

                {/* Children */}
                {menuItem.children && menuItem.children.length > 0 && (
                  <ul
                    className="sidebar-submenu"
                    style={
                      layout1 !== "compact-sidebar compact-small"
                        ? isSubmenuOpen
                          ? { display: "block" }
                          : { display: "none" }
                        : { display: "none" }
                    }
                  >
                    {menuItem.children.map((child, k) => (
                      <li key={k}>
                        <Tooltip
                          id={`tooltip-${child.title + k}`}
                          place="top"
                          style={{ backgroundColor: "#eaeaeb", color: "#000" }}
                          content={child.title}
                        />
                        <Link
                          to={child.path}
                          className={isPathActive(child.path) ? "active" : ""}
                          onClick={handleNavClick}
                          data-tooltip-id={`tooltip-${child.title + k}`}
                        >
                          {t(child.title)}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </Fragment>
      ))}
    </>
  );
};

export default SidebarMenuItems;
